import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import {
  MrpUyarilari, MrpEksikHesapNotu, MrpEldeNotu, mrpHesabiEksik, mrpEldeNotOzeti, yeterliMetni,
  MRP_ELDE_TANIMI, MRP_KAYIT_ACIGI_TANIMI, type MrpUyariVerisi, type MrpEldeUyarisi,
} from "../components/mrp/MrpUyarilari";
import { OrderMrpSelector } from "../components/mrp/OrderMrpSelector";
import { GENEL_MRP_KUME } from "../lib/siparisDurum";
import "./Items.css";
import "./Mrp.css";

interface Suggestion {
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  grossRequirement: number;
  /** Kullanılabilir NET elde (eksiler düşülmüş, ≥ 0) — bkz. api `mrp.ts` → `eldeHaritasi`. */
  onHand: number;
  /** SKT'si geçmiş lotlarda duran miktar — elde SAYILMAZ, ayrıca gösterilir. */
  expiredQty: number;
  /** QC bekleyen / karantina / red lotlardaki miktar — elde SAYILMAZ, ayrıca gösterilir. */
  blockedQty: number;
  /** Eksiler kullanılabilir stoğu aştıysa aşan kısım (kayıt açığı) — satın alma ihtiyacına EKLENMEZ. */
  deficitQty: number;
  onOrder: number;
  reorderPoint: number | null;
  netRequirement: number;
  supplierId: string | null;
  supplierName: string | null;
  leadTimeDays: number | null;
}

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

/** Hesap zamanı — TR tarih + saat (13.05.2026 14:32). */
function fmtZaman(iso: string): string {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

/**
 * ELDE KIRILIMI — `runMrp` alan adları → paylaşılan elde notunun (`MrpEldeNotu`) alan adları.
 *
 * ⚠ AYNA KURALI: bu ekranda bir dönem KENDİ not bileşeni ve tanım metni vardı (eşik `> 0`, farklı
 * renk ve etiket) — aynı kalem sipariş kartında ve burada farklı not taşıyordu. Artık not, eşik,
 * renk, ikon ve tanım metni `mrp/MrpUyarilari`dan gelir; burada YALNIZ ad eşlemesi yapılır.
 * ⚠ SESSİZLİK YASAĞI: SKT'si geçmiş / karantinadaki stok elde sayılmıyor; yazılmazsa kullanıcı
 * "stokta var ama MRP görmüyor" der (canlı olay: AMB-RSTRNT-01 — MRP 0, Stok Genel Bakış 25.760).
 */
function eldeKirilimi(x: { expiredQty: number; blockedQty: number; deficitQty: number }) {
  return { sktGecmis: x.expiredQty, karantina: x.blockedQty, kayitAcigi: x.deficitQty };
}

/**
 * GENEL MRP KAPSAMI — "MRP Çalıştır"ın talep topladığı durumlar, ekranda DURUM ADLARIYLA yazılır.
 *
 * ⚠ "Açık sipariş" DENMEZ: aynı sayfadaki "Seçili Siparişler için MRP" kartında "Açık siparişler"
 * kısayolu BAŞKA bir kümedir (Taslak · Onaylı · Üretimde); genel MRP ise Onaylı · Üretimde · Hazır
 * siparişleri sayar. İki küme aynı adla anılırsa kullanıcı kısayolla genel MRP'nin kapsamını
 * seçtiğini sanır. Küme sabit metin olarak KOPYALANMAZ: `GENEL_MRP_KUME` sunucudaki
 * `MRP_TALEP_DURUMLARI`nın (api `mrp.ts`) aynasıdır — parite `test-order-status-type-filter.ts` bölüm L.
 * Sayfanın üç yeri (alt başlık, boş durum, sayaç) masaüstü ve mobilde bu iki parçadan basar.
 */
const CALISTIR_IPUCU = `"MRP Çalıştır" ile ${GENEL_MRP_KUME} durumundaki siparişlere göre hammadde ihtiyacını hesaplayın.`;

/** Sayaç: "N talep siparişi (Onaylı · Üretimde · Hazır) · M öneri" — `demandOrderCount` = mrp.ts talep kümesi. */
function TalepSayaci({ siparis, oneri }: { siparis: number; oneri: number }) {
  return (
    <>
      <b className="mono">{siparis}</b> talep siparişi ({GENEL_MRP_KUME}) · <b className="mono">{oneri}</b> öneri
    </>
  );
}

export function MrpPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [ran, setRan] = useState(false);
  // Satır bazında: seçim, miktar override, tedarikçi override
  const [sel, setSel] = useState<Record<string, { checked: boolean; qty: string; supplierId: string }>>({});
  const [result, setResult] = useState<{ created: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const mrpQ = trpc.purchasing.runMrp.useQuery(undefined, { enabled: ran, staleTime: 0 });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100 });
  const createMut = trpc.purchasing.createPosFromSuggestions.useMutation();

  // Cast YOK: sunucu tipi (`MrpSuggestion`) yerel arayüze yapısal olarak uymazsa (ör. elde kırılımı
  // alanları kalkarsa) tsc burada yakalar — eskiden `as unknown as` kaymayı sessizce yutuyordu.
  const suggestions: Suggestion[] = mrpQ.data?.suggestions ?? [];
  /**
   * ⚠ SESSİZLİK YASAĞI: `runMrp` hesaba KATAMADIĞI kalemleri ayrıca döndürür (mrp.ts:
   * `recetesizUrunler` / `atlananKalemler` / `patlatilamayanBilesenler`). Bu ekran onları
   * okumadığı sürece boş öneri listesi "her şey yeterli" diye okunurdu — kullanıcının canlıda
   * takıldığı kusurun ta kendisi. Aynı listeleri sipariş kartı da AYNI bileşenle basar (ayna kuralı).
   */
  const uyariVeri: MrpUyariVerisi | undefined = mrpQ.data;
  /** Hesap eksikse yeterlilik iddiası KURULAMAZ — "tüm hammaddeler yeterli" yazısı bastırılır. */
  const hesapEksik = mrpHesabiEksik(uyariVeri);
  const mrpDisiKalemler = mrpQ.data?.mrpDisiKalemler;
  /**
   * ELDE UYARILARI — net ihtiyacı OLMAYAN (öneri listesine girmeyen) ama SKT geçmiş / karantina stoğu ya
   * da kayıt açığı olan kalemler (mrp.ts `eldeUyarilari`). Öneri tablosunda yoklar; "Eksik yok ama dikkat"
   * kutusunda sipariş kartıyla AYNI bileşenle yazılırlar. Ara değişken TİPLİ: sunucu alanı yeniden
   * adlandırılırsa tsc burada yakalar (cast yok).
   */
  const eldeUyarilari: MrpEldeUyarisi[] = useMemo(() => {
    const ham: Array<{ code: string; name: string; uom: string; expiredQty: number; blockedQty: number; deficitQty: number }> =
      mrpQ.data?.eldeUyarilari ?? [];
    return ham.map((x) => ({ code: x.code, name: x.name, uom: x.uom, ...eldeKirilimi(x) }));
  }, [mrpQ.data]);
  /** Elde tanımı (ve kayıt açığı tanımı) ne zaman yazılır — sipariş kartıyla AYNI yardımcı. */
  const eldeOzeti = mrpEldeNotOzeti([...suggestions.map(eldeKirilimi), ...eldeUyarilari]);
  /** Öneri listesi boşken basılan cümle — sipariş kartı ve A4 raporla AYNI kaynak (MRP dışı/elde uyarısında daralır). */
  const yeterli = yeterliMetni({ mrpDisiKalemler, eldeUyarilari });
  /**
   * HESAP HATASI — `runMrp` patlarsa SUSULMAZ. Veri yokken hata, "satınalma gerekmiyor" boş durumuna
   * DÜŞMEMELİ (eskiden düşüyordu: hata = boş öneri = "tüm hammaddeler yeterli"). Önceki sonuç
   * duruyorsa üstünde "son hesap başarısız" bandı basılır — eski liste güncelmiş gibi okunmasın.
   */
  const hesapHatasi = ran && mrpQ.isError ? (mrpQ.error?.message || "Bilinmeyen hata") : null;

  // İlk yüklemede sel default'larını kur
  const selInit = useMemo(() => {
    const m: Record<string, { checked: boolean; qty: string; supplierId: string }> = {};
    for (const s of suggestions) m[s.itemId] = { checked: true, qty: String(s.netRequirement), supplierId: s.supplierId ?? "" };
    return m;
  }, [suggestions]);

  const effectiveSel = (itemId: string) => sel[itemId] ?? selInit[itemId] ?? { checked: false, qty: "0", supplierId: "" };

  function setRow(itemId: string, patch: Partial<{ checked: boolean; qty: string; supplierId: string }>) {
    setSel((s) => ({ ...s, [itemId]: { ...effectiveSel(itemId), ...patch } }));
  }

  function runMrp() {
    setRan(true);
    setResult(null);
    setErr(null);
    createMut.reset();
    // Satır düzenlemeleri burada SİLİNMEZ: hesap patlarsa eski liste ekranda kalır ve kullanıcının
    // düzenlemeleri kaybolurdu (V10). Seçim yalnız YENİ sonuç geldiğinde sıfırlanır (aşağıdaki effect).
    mrpQ.refetch();
  }
  const sonucDamgasi = mrpQ.data?.generatedAt;
  useEffect(() => { setSel({}); }, [sonucDamgasi]);

  async function createPos() {
    setErr(null);
    const items = suggestions
      .map((s) => ({ s, row: effectiveSel(s.itemId) }))
      .filter(({ row }) => row.checked && Number(row.qty) > 0)
      .map(({ s, row }) => ({ itemId: s.itemId, qty: Number(row.qty), supplierId: row.supplierId || null }));
    if (items.length === 0) { setErr("En az bir kalem seçin (miktarı 0'dan büyük)."); return; }
    const noSup = items.filter((i) => !i.supplierId);
    if (noSup.length > 0) { setErr(`${noSup.length} kalemin tedarikçisi seçili değil. Her seçili kalem için tedarikçi seçin.`); return; }
    try {
      const res = await createMut.mutateAsync({ items });
      await qc.invalidateQueries();
      setResult({ created: res.created.length });
      setRan(false);
    } catch {
      /* Hata createMut.error üzerinden banner'da gösteriliyor. */
    }
  }

  const suppliers = suppliersQ.data?.rows ?? [];

  const hataBandi = hesapHatasi ? (
    <div className="form-banner-err mrp-hata" role="alert">
      <Icon name="alertTriangle" size={14} />
      {mrpQ.data ? (
        <span>
          <b>Son hesap başarısız</b> — {hesapHatasi}. Aşağıdaki liste{" "}
          <span className="mono">{fmtZaman(mrpQ.data.generatedAt)}</span> tarihli ÖNCEKİ hesaptır; güncel stok
          ve talebi yansıtmayabilir. Yeniden denemek için “MRP Çalıştır”.
        </span>
      ) : (
        <span>
          <b>MRP hesaplanamadı</b> — {hesapHatasi}. Sonuç yok: satınalma ihtiyacı hakkında bir şey
          söylenemez. Yeniden denemek için “MRP Çalıştır”.
        </span>
      )}
    </div>
  ) : null;

  /**
   * "Elde" TANIMI — başlık ipucu (title) mobilde görünmez, tanım GÖRÜNÜR satır olarak da yazılır.
   * Metin paylaşılan sabitlerden (sipariş kartı dipnotu ve A4 raporla aynı); kayıt açığı tanımı
   * yalnız bir satırda kayıt açığı notu varsa eklenir (`mrpEldeNotOzeti`).
   */
  const eldeTanimi = suggestions.length > 0 || eldeOzeti.notVar ? (
    <div className="mn-msg">
      <Icon name="info" size={14} />
      <span>
        <b>Elde</b> = {MRP_ELDE_TANIMI}.
        {eldeOzeti.acikVar ? <> <b>Kayıt açığı</b> = {MRP_KAYIT_ACIGI_TANIMI}.</> : null}
      </span>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={runMrp} disabled={mrpQ.isFetching}>
            {mrpQ.isFetching ? "Hesaplanıyor…" : <><Icon name="refresh" size={15} />MRP Çalıştır</>}
          </button>
          {ran && suggestions.length > 0 ? (
            <button type="button" className="btn-md secondary" onClick={createPos} disabled={createMut.isPending || !!hesapHatasi} title={hesapHatasi ? "Son hesap başarısız — önce MRP'yi yeniden çalıştırın" : undefined}>
              {createMut.isPending ? "Oluşturuluyor…" : "Seçili Kalemlerden PO Oluştur"}
            </button>
          ) : null}
        </div>

        {/* Mobil dal AYRI render ediyor — buraya da eklenmezse özellik mobilde YOK olur. */}
        <OrderMrpSelector />

        {result ? (
          <div style={{ padding: "10px 14px", background: "var(--success-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--success-text)" }}>
            <b>{result.created}</b> taslak PO oluşturuldu. <Link to="/purchasing" style={{ color: "var(--primary)", fontWeight: 600 }}>Satınalma →</Link>
          </div>
        ) : null}
        {(err || createMut.error) ? (
          <div className="form-banner-err" role="alert">{err ?? createMut.error?.message}</div>
        ) : null}
        {hataBandi}

        {!ran ? (
          <div className="m-empty">{CALISTIR_IPUCU}</div>
        ) : !mrpQ.data ? (
          // Veri YOK: ya hesaplanıyor ya da hesap patladı (bant yukarıda). Hiçbir durumda "yeterli" denmez.
          hesapHatasi ? null : <div className="m-empty">Hesaplanıyor…</div>
        ) : (
          <>
            <MrpUyarilari veri={uyariVeri} mrpDisiKalemler={mrpDisiKalemler} eldeUyarilari={eldeUyarilari} />
            {eldeTanimi}
            {suggestions.length === 0 ? (
              hesapEksik ? (
                <MrpEksikHesapNotu metin="Hesaba GİREN kalemlerde satınalma ihtiyacı çıkmadı — ancak yukarıdaki kalemler hesaba katılamadığı için bu sonuç TAM DEĞİL; yeterlilik iddiasında bulunulamaz." />
              ) : (
                <div className="m-empty">{yeterli}</div>
              )
            ) : (
              <>
                <div className="m-ovl" style={{ padding: "0 2px" }}>
                  <TalepSayaci siparis={mrpQ.data.demandOrderCount} oneri={suggestions.length} />
                </div>
                {suggestions.map((s) => {
                  const row = effectiveSel(s.itemId);
                  return (
                    <MCard pad key={s.itemId} className={`mrp-mkart${row.checked ? " sel" : ""}`}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <button type="button" className="m-cardcheck" aria-label={`${s.itemName} seç`} aria-pressed={row.checked} onClick={() => setRow(s.itemId, { checked: !row.checked })}>{row.checked ? <Icon name="check" size={14} /> : null}</button>
                        <span style={{ flex: 1, minWidth: 0 }} onClick={() => setRow(s.itemId, { checked: !row.checked })}>
                          <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{s.itemName}</span>
                          <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{s.itemCode}</span>
                        </span>
                        <span style={{ textAlign: "right" }}>
                          <span className="m-ovl">Net İhtiyaç</span>
                          <span className="mono" style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--warning)" }}>{fmtQty(s.netRequirement)} {s.uom}</span>
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                        {([["Brüt", `${fmtQty(s.grossRequirement)}`], ["Elde", `${fmtQty(s.onHand)}`], ["Açık PO", `${fmtQty(s.onOrder)}`], ["Sip. Nok.", s.reorderPoint ? fmtQty(s.reorderPoint) : "—"]] as Array<[string, string]>).map(([lb, v]) => (
                          <div key={lb} style={{ flex: 1 }}>
                            <div className="m-ovl">{lb}</div>
                            <div className="mono" style={{ fontSize: 12, fontWeight: 600, color: "var(--neutral-700)", marginTop: 2 }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {/* Elde kırılımı dar "Elde" kolonuna sıkıştırılmaz: kartın tam genişliğinde, sarabilen satır
                          (sipariş kartındaki mobil kartla AYNI bileşen ve yerleşim). */}
                      <MrpEldeNotu {...eldeKirilimi(s)} uom={s.uom} yerlesim="satir" />
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <label style={{ flex: "0 0 110px" }}>
                          <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Sipariş Mik.</span>
                          <input className="form-input" type="number" step="any" min={0} value={row.qty} onChange={(e) => setRow(s.itemId, { qty: e.target.value })} inputMode="decimal" aria-label="Sipariş miktarı" />
                        </label>
                        <label style={{ flex: 1, minWidth: 0 }}>
                          <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Tedarikçi</span>
                          <select className="form-select" value={row.supplierId} onChange={(e) => setRow(s.itemId, { supplierId: e.target.value })} aria-label="Tedarikçi">
                            <option value="">— Tedarikçi seç —</option>
                            {suppliers.map((sup) => <option key={sup.id} value={sup.id}>{sup.code} — {sup.name}</option>)}
                          </select>
                        </label>
                      </div>
                    </MCard>
                  );
                })}
              </>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">MRP / Planlama</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">MRP / Malzeme İhtiyaç Planlama</h1>
          <p className="sub">Sipariş talebi ({GENEL_MRP_KUME}) → BOM patlatma → net hammadde ihtiyacı → satınalma önerisi</p>
        </div>
        <button type="button" className="btn-md primary" onClick={runMrp} disabled={mrpQ.isFetching}>
          {mrpQ.isFetching ? "Hesaplanıyor…" : <><Icon name="refresh" size={15} />MRP Çalıştır</>}
        </button>
      </div>

      {/* Sipariş seçimi + A4 rapor. Üstteki "MRP Çalıştır" GENEL_MRP_KUME (Onaylı · Üretimde · Hazır)
          durumundaki TÜM siparişleri kapsar — "açık siparişler" DEĞİL (o, kartın kısayolundaki ayrı küme);
          bu kart SEÇİLİ siparişlere özel koşu içindir (yalnız `view:recipe` izni olana görünür). */}
      <OrderMrpSelector />

      {result ? (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "var(--success-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--success-text)" }}>
          <b>{result.created}</b> taslak satınalma siparişi oluşturuldu (tedarikçiye göre gruplandı). <Link to="/purchasing" style={{ color: "var(--primary)", fontWeight: 500 }}>Satınalma siparişlerine git →</Link>
        </div>
      ) : null}

      {(err || createMut.error) ? (
        <div className="form-banner-err" role="alert" style={{ marginBottom: 12 }}>{err ?? createMut.error?.message}</div>
      ) : null}
      {hataBandi}

      {!ran ? (
        <div className="dt2-wrap">
          <div className="empty-state">
            <p className="ttl">MRP çalıştırılmadı</p>
            <p className="sub">{CALISTIR_IPUCU}</p>
          </div>
        </div>
      ) : hesapHatasi && !mrpQ.data ? null : (
        // Veri yokken hesap patladıysa yukarıdaki bant TEK cevaptır: tablo/boş durum ("yeterli") BASILMAZ.
        <div className="dt2-wrap">
          <div className="dt2-toolbar">
            <span style={{ fontSize: 13, color: "var(--neutral-600)" }}>
              {mrpQ.data ? <TalepSayaci siparis={mrpQ.data.demandOrderCount} oneri={suggestions.length} /> : "…"}
            </span>
            <span className="grow" />
            <button type="button" className="btn primary" onClick={createPos} disabled={createMut.isPending || suggestions.length === 0 || !!hesapHatasi} title={hesapHatasi ? "Son hesap başarısız — önce MRP'yi yeniden çalıştırın" : undefined}>
              {createMut.isPending ? "Oluşturuluyor…" : "Seçili Kalemlerden PO Oluştur"}
            </button>
          </div>

          {mrpQ.data ? (
            <>
              <MrpUyarilari veri={uyariVeri} mrpDisiKalemler={mrpDisiKalemler} eldeUyarilari={eldeUyarilari} />
              {eldeTanimi}
            </>
          ) : null}

          <div className="dt2-scroll">
            {!mrpQ.data ? (
              // Veri yok = hesaplanıyor (hata dalı yukarıda ayrıldı). Boş öneri listesi ASLA "yeterli" sayılmaz.
              <div className="empty-state"><p className="ttl">Hesaplanıyor…</p></div>
            ) : suggestions.length === 0 ? (
              // "Yeterli" ancak hesap TAM ise söylenir: motorun atladığı TEK bir kalem bile
              // varsa (reçetesiz ürün, patlatmaya girmeyen satır, patlatılamayan dal) bu
              // ekran hammaddelerin yeterli olduğunu İDDİA EDEMEZ.
              hesapEksik ? (
                <MrpEksikHesapNotu metin="Hesaba GİREN kalemlerde satınalma ihtiyacı çıkmadı — ancak yukarıdaki kalemler hesaba katılamadığı için bu sonuç TAM DEĞİL; yeterlilik iddiasında bulunulamaz." />
              ) : (
                <div className="empty-state"><p className="ttl">Satınalma önerisi yok</p><p className="sub">{yeterli}</p></div>
              )
            ) : (
              <table className="dt2">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th>Hammadde</th>
                    <th className="num" style={{ width: 90 }}>Brüt</th>
                    <th className="num" style={{ width: 90 }} title={`Elde = ${MRP_ELDE_TANIMI}`}>Elde</th>
                    <th className="num" style={{ width: 90 }}>Açık PO</th>
                    <th className="num" style={{ width: 96 }} title="Yeniden sipariş / emniyet stok noktası. Net = Brüt + Sip.Noktası − Elde − Açık PO">Sip. Noktası</th>
                    <th className="num" style={{ width: 100 }}>Net İhtiyaç</th>
                    <th style={{ width: 110 }}>Sipariş Mik.</th>
                    <th style={{ width: 180 }}>Tedarikçi</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => {
                    const row = effectiveSel(s.itemId);
                    return (
                      <tr key={s.itemId}>
                        <td style={{ paddingLeft: 14 }}>
                          <input type="checkbox" className="cb" checked={row.checked} onChange={(e) => setRow(s.itemId, { checked: e.target.checked })} aria-label="Seç" />
                        </td>
                        <td>{s.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{s.itemCode}</span></td>
                        <td className="cell-num">{fmtQty(s.grossRequirement)}<span className="unit">{s.uom}</span></td>
                        <td className="cell-num mrp-soluk">
                          {fmtQty(s.onHand)}
                          <MrpEldeNotu {...eldeKirilimi(s)} uom={s.uom} />
                        </td>
                        <td className="cell-num mrp-soluk">{fmtQty(s.onOrder)}</td>
                        <td className="cell-num" style={{ color: s.reorderPoint ? "var(--info)" : "var(--neutral-400)" }}>{s.reorderPoint ? fmtQty(s.reorderPoint) : "—"}</td>
                        <td className="cell-num" style={{ fontWeight: 600, color: "var(--warning)" }}>{fmtQty(s.netRequirement)}<span className="unit">{s.uom}</span></td>
                        <td>
                          <input className="form-input" type="number" step="any" min={0} value={row.qty} onChange={(e) => setRow(s.itemId, { qty: e.target.value })} style={{ height: 30, fontSize: 12.5 }} inputMode="decimal" aria-label="Sipariş miktarı" />
                        </td>
                        <td>
                          <select className="form-select" value={row.supplierId} onChange={(e) => setRow(s.itemId, { supplierId: e.target.value })} style={{ height: 30, fontSize: 12 }} aria-label="Tedarikçi">
                            <option value="">— Tedarikçi seç —</option>
                            {suppliers.map((sup) => <option key={sup.id} value={sup.id}>{sup.code} — {sup.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
